"use client";

import { useRef } from "react";
import type { PointerEvent, ReactNode } from "react";
import Card from "./Card";

/** Cursor-tracked glow card: soft radial highlight follows the pointer via CSS vars
 *  (paint-only, no transforms). Gated to fine-pointer devices via media query, bails
 *  on touch to avoid stuck highlight. Position seeds on pointer enter to avoid stale
 *  glow if hover activates without a move. No tilt/displacement — readable-first. */
export default function GlowCard({
  span = "",
  children,
}: {
  span?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  const setGlowPos = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "touch") return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <div ref={ref} data-glow-card onPointerEnter={setGlowPos} onPointerMove={setGlowPos} className={`group relative ${span}`}>
      <Card shadow className="relative h-full overflow-hidden px-6 py-5">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(180px circle at var(--mx, 50%) var(--my, 50%), rgba(215,14,98,0.10), transparent 70%)",
          }}
        />
        <div className="relative">{children}</div>
      </Card>
    </div>
  );
}
